import type {
  ActiveBossEffect,
  BossEffectKind,
  BossState,
  GameEvent,
  GameState,
  MobStats,
  MobType,
  PlayerTributeSummary,
  TargetingMode,
  TowerType,
} from "@mmotdg/shared";
import {
  BASE_LOADOUT,
  BOSS_EFFECT_DEFINITIONS,
  MOB_DEFINITIONS,
  TICK_RATE,
  TOWER_DEFINITIONS,
  buildWave,
  scaleMobStats,
} from "@mmotdg/shared";
import { MatchController } from "../game/MatchController.js";
import { mobPreviewPool } from "../game/MobPreview.js";
import { towerPreviewPool } from "../game/TowerPreview.js";
import { gameSocket } from "../network/WebSocketClient.js";
import { appState } from "../state/AppState.js";
import { icon } from "./icons.js";
import { invalidateTutorialCache } from "./LobbyScreen.js";

let controller: MatchController | null = null;
let currentState: GameState | null = null;
let eventLogContainer: HTMLDivElement | null = null;
let dmgLayer: HTMLDivElement | null = null;
let bannerLayer: HTMLDivElement | null = null;
let lastNextWaveIndex = -1;
let waveStartedAt = 0;
let waveExpectedDurationMs = 0;
let waveTotalMobs = 0;
let waveKilled = 0;
let lastWaveInProgress = false;
let matchStats = {
  damageDealt: 0,
  mobsKilled: 0,
  towersBuilt: 0,
  satsSpent: 0,
};
let hotkeyHandler: ((e: KeyboardEvent) => void) | null = null;
let loadoutCache: TowerType[] = [];
let tributesByUser: PlayerTributeSummary[] = [];
let bossEffectsAnimHandle: number | null = null;
let wasAlive = true;
let matchContainer: HTMLElement | null = null;
let labelLayer: HTMLDivElement | null = null;
let labelRafHandle: number | null = null;
const playerLabelEls = new Map<string, HTMLDivElement>();

type Severity = "info" | "success" | "warning" | "danger" | "boss";

export async function renderMatchScreen(container: HTMLElement): Promise<void> {
  const assignment = appState.get().matchAssignment;
  const user = appState.get().user;
  const token = appState.get().token;
  if (!assignment || !user || !token) {
    appState.set({ screen: "lobby" });
    return;
  }

  container.innerHTML = `
    <canvas id="game-canvas"></canvas>
    <div class="hud">
      <div class="hud-left">
        <div class="hud-stat">
          ${icon("user", 16)}
          <div>
            <div class="label">Player</div>
            <div class="value" id="hud-name">${escapeHtml(user.username)}</div>
          </div>
        </div>
        <div class="hud-stat sats">
          ${icon("sats", 18)}
          <div>
            <div class="label">Sats</div>
            <div class="value" id="hud-gold">0</div>
          </div>
        </div>
        <div class="hud-stat income" title="Income per wave">
          ${icon("economy", 16)}
          <div>
            <div class="label">Income</div>
            <div class="value" id="hud-income">0</div>
          </div>
        </div>
      </div>
      <div class="hud-center">
        <div class="wave-tracker idle" id="wave-tracker">
          <div class="row">
            <span class="label">Wave</span>
            <span class="value" id="wave-value">0</span>
          </div>
          <div class="row">
            <span class="label" id="wave-status-label">Prepare</span>
            <span class="value" id="wave-meta">—</span>
          </div>
          <div class="progress"><div id="wave-progress-fill"></div></div>
        </div>
      </div>
      <div class="hud-right">
        <div class="base-hp" id="base-hp">
          ${icon("hp", 16)}
          <div>
            <div class="label">Base</div>
            <div class="value" id="hud-hp">0</div>
          </div>
          <div class="bar" id="base-hp-bar" style="width:100%"></div>
        </div>
        <button id="ready-btn" class="primary cta-pulse" title="Start the next wave (Space)">Start wave <span style="opacity:0.65;margin-left:6px;">[SPACE]</span></button>
        <button id="leave-btn" class="danger">Leave</button>
      </div>
    </div>
    <div class="boss-hud" id="boss-hud" hidden>
      <div class="boss-header">
        <span class="boss-name" id="boss-name">—</span>
        <span class="boss-meta" id="boss-meta">—</span>
      </div>
      <div class="boss-hp">
        <div class="boss-hp-bar"><div class="boss-hp-fill" id="boss-hp-fill"></div></div>
        <div class="boss-hp-marker" style="left:25%"></div>
        <div class="boss-hp-marker" style="left:50%"></div>
        <div class="boss-hp-marker" style="left:75%"></div>
        <div class="boss-hp-value" id="boss-hp-value">0 / 0</div>
      </div>
      <div class="boss-effects" id="boss-effects"></div>
      <div class="boss-tributes" id="boss-tributes"></div>
    </div>
    <div class="next-wave" id="next-wave"></div>
    <div class="tower-panel" id="tower-panel"></div>
    <div class="event-log" id="event-log"></div>
    <div class="dmg-layer" id="dmg-layer" style="position:absolute;inset:0;pointer-events:none;z-index:14;"></div>
    <div class="player-labels" id="player-labels" style="position:absolute;inset:0;pointer-events:none;z-index:18;"></div>
    <div class="banner-layer" id="banner-layer" style="position:absolute;inset:0;pointer-events:none;z-index:50;"></div>
  `;

  const canvas = container.querySelector<HTMLCanvasElement>("#game-canvas")!;
  eventLogContainer = container.querySelector<HTMLDivElement>("#event-log");
  dmgLayer = container.querySelector<HTMLDivElement>("#dmg-layer");
  labelLayer = container.querySelector<HTMLDivElement>("#player-labels");
  bannerLayer = container.querySelector<HTMLDivElement>("#banner-layer");
  startPlayerLabelLoop(user.id);

  matchStats = { damageDealt: 0, mobsKilled: 0, towersBuilt: 0, satsSpent: 0 };
  waveStartedAt = 0;
  waveExpectedDurationMs = 0;
  waveTotalMobs = 0;
  waveKilled = 0;
  lastWaveInProgress = false;
  lastNextWaveIndex = -1;
  wasAlive = true;
  matchContainer = container;

  tributesByUser = [];
  controller = new MatchController(canvas, user.id, {
    onStateChange: (state) => {
      currentState = state;
      updateHUD(state, user.id);
      refreshNextWave(container, state);
      refreshTowerInfo(container);
      syncTowerTileLocked(container);
      syncTowerTileSelection(container);
      syncTowerTileAffordability(container, state, user.id);
      maybeShowWaveTransition(state);
      renderBossHUD(state);
      maybeAutoSpectate(container, state, user.id);
    },
    onEvent: (event) => handleGameEvent(event),
    onGameOver: (winnerId) => showGameOver(winnerId, user.id),
    onChat: () => {},
    onError: (code) => appendEvent(`Error: ${code}`, "danger"),
    onDamage: (worldX, worldY, amount, isKill) => spawnDamageNumber(worldX, worldY, amount, isKill),
    onTowerFired: (towerId) => controller?.renderTowerFireFlash(towerId),
    onBossState: (boss) => {
      if (currentState) currentState.bossState = boss;
      renderBossHUD(currentState);
    },
    onBossEffectApplied: (effect) => {
      appendEvent(`Boss effect: ${BOSS_EFFECT_DEFINITIONS[effect.kind].label}`, "boss");
      showBanner("Boss", BOSS_EFFECT_DEFINITIONS[effect.kind].label, "boss");
    },
    onBossEffectExpired: (kind) => {
      appendEvent(`Boss effect expired: ${BOSS_EFFECT_DEFINITIONS[kind].label}`, "info");
    },
    onTributeUpdate: (upd) => {
      tributesByUser = upd.tributes;
      renderBossHUD(currentState);
      if (upd.playerId === user.id) {
        appendEvent(`+${upd.delta} tribute (total ${upd.total})`, "success");
      }
    },
    onStakeTransfer: (info) => {
      const total = info.splits.reduce((sum, s) => sum + s.amount, 0);
      appendEvent(`Stake transfer (${info.reason}): ${total} sats inherited`, "boss");
    },
  });

  try {
    await gameSocket.connect(assignment.serverHost, assignment.serverPort, token, assignment.roomId);
  } catch {
    appendEvent("Connection failed", "danger");
    setTimeout(() => appState.set({ screen: "lobby" }), 2000);
    return;
  }

  renderTowerPanel(container);
  setupHotkeys(container);

  container.querySelector("#ready-btn")!.addEventListener("click", () => controller?.sendReady());
  container.querySelector("#leave-btn")!.addEventListener("click", () => {
    gameSocket.send({ type: "leave_match" });
    leaveMatch();
  });
}

function updateHUD(state: GameState, userId: string): void {
  const me = state.players.find((p) => p.id === userId);
  if (me) {
    const goldEl = document.getElementById("hud-gold");
    if (goldEl) goldEl.textContent = me.gold.toLocaleString();
    const incomeEl = document.getElementById("hud-income");
    if (incomeEl) incomeEl.textContent = me.income.toString();
  }
  const waveEl = document.getElementById("wave-value");
  if (waveEl) waveEl.textContent = String(state.currentWave);

  const myLane = me ? state.lanes.find((l) => l.id === me.laneId) : null;
  const lane = myLane ?? state.lanes[0];
  if (lane) {
    const hpEl = document.getElementById("hud-hp");
    if (hpEl) hpEl.textContent = `${lane.baseHp}/${lane.baseMaxHp}`;
    const ratio = Math.max(0, lane.baseHp / lane.baseMaxHp);
    const bar = document.getElementById("base-hp-bar");
    if (bar) bar.style.width = `${ratio * 100}%`;
    const baseHpEl = document.getElementById("base-hp");
    if (baseHpEl) {
      baseHpEl.classList.toggle("warning", ratio > 0.3 && ratio <= 0.55);
      baseHpEl.classList.toggle("critical", ratio > 0 && ratio <= 0.3);
    }
  }

  const tracker = document.getElementById("wave-tracker");
  const statusLabel = document.getElementById("wave-status-label");
  const meta = document.getElementById("wave-meta");
  const fill = document.getElementById("wave-progress-fill");
  const readyBtn = document.getElementById("ready-btn") as HTMLButtonElement | null;
  if (tracker && statusLabel && meta && fill) {
    if (state.waveInProgress) {
      tracker.classList.remove("idle");
      const killed = Math.min(waveKilled, waveTotalMobs);
      statusLabel.textContent = "In progress";
      meta.textContent = waveTotalMobs > 0 ? `${killed}/${waveTotalMobs}` : "—";
      const elapsed = performance.now() - waveStartedAt;
      const byTime = waveExpectedDurationMs > 0 ? elapsed / waveExpectedDurationMs : 0;
      const byKills = waveTotalMobs > 0 ? killed / waveTotalMobs : 0;
      const pct = Math.min(100, Math.max(byTime, byKills) * 100);
      fill.style.width = `${pct}%`;
      if (readyBtn) {
        readyBtn.disabled = true;
        readyBtn.classList.remove("cta-pulse");
      }
    } else {
      tracker.classList.add("idle");
      statusLabel.textContent = "Prepare";
      meta.textContent = "Ready";
      if (readyBtn) {
        readyBtn.disabled = false;
        readyBtn.classList.add("cta-pulse");
      }
    }
  }
}

function renderBossHUD(state: GameState | null): void {
  const hud = document.getElementById("boss-hud");
  if (!hud) return;
  const boss = state?.bossState;
  if (!state || !boss) {
    hud.hidden = true;
    stopBossEffectsTicker();
    return;
  }
  hud.hidden = false;

  const nameEl = document.getElementById("boss-name");
  const metaEl = document.getElementById("boss-meta");
  if (nameEl) nameEl.textContent = bossDisplayName(boss.bossId);
  if (metaEl) {
    const phaseTotal = 4;
    metaEl.textContent = `${boss.difficulty.toUpperCase()} · Phase ${boss.currentPhase + 1}/${phaseTotal}${
      boss.variant === "endurance" ? " · ENDURANCE" : ""
    }`;
  }

  const hpFill = document.getElementById("boss-hp-fill") as HTMLDivElement | null;
  const hpValue = document.getElementById("boss-hp-value");
  if (hpFill) {
    const ratio = boss.hpMax > 0 ? boss.hp / boss.hpMax : 0;
    hpFill.style.width = `${Math.max(0, Math.min(100, ratio * 100))}%`;
    hpFill.classList.toggle("phase-0", boss.currentPhase === 0);
    hpFill.classList.toggle("phase-1", boss.currentPhase === 1);
    hpFill.classList.toggle("phase-2", boss.currentPhase === 2);
    hpFill.classList.toggle("phase-3", boss.currentPhase === 3);
  }
  if (hpValue) {
    hpValue.textContent = boss.variant === "endurance"
      ? "∞"
      : `${boss.hp.toLocaleString()} / ${boss.hpMax.toLocaleString()}`;
  }

  renderBossEffects(boss.activeEffects, state.tick);

  // Schedule a per-frame update of effect countdowns while there are active
  // ones. The boss HP itself only changes on tribute_update / boss_state.
  if (boss.activeEffects.length > 0) {
    startBossEffectsTicker();
  } else {
    stopBossEffectsTicker();
  }

  renderBossTributes(state);
}

function renderBossEffects(effects: ActiveBossEffect[], currentTick: number): void {
  const root = document.getElementById("boss-effects");
  if (!root) return;
  if (effects.length === 0) {
    root.innerHTML = `<span class="boss-effect-empty">No active effect</span>`;
    return;
  }
  const html = effects.map((eff) => {
    const def = BOSS_EFFECT_DEFINITIONS[eff.kind];
    const remaining = Math.max(0, eff.endsAtTick - currentTick);
    const secs = (remaining / TICK_RATE).toFixed(1);
    const isInstant = def.durationTicks === 0;
    const family = def.family;
    return `<span class="boss-effect family-${family}" data-kind="${eff.kind}">
      <span class="boss-effect-name">${escapeHtml(def.label)}</span>
      ${isInstant ? "" : `<span class="boss-effect-time">${secs}s</span>`}
    </span>`;
  }).join("");
  root.innerHTML = html;
}

function renderBossTributes(state: GameState): void {
  const root = document.getElementById("boss-tributes");
  if (!root) return;

  // Build one row per player so the panel always shows everyone's base HP
  // (and tribute, when they've started earning it).
  const tributeMap = new Map(tributesByUser.map((t) => [t.userId, t.totalTribute]));
  const totalTribute = tributesByUser.reduce((sum, t) => sum + t.totalTribute, 0);
  const players = state.players.filter((p) => p.laneId);
  if (players.length === 0) {
    root.innerHTML = `<span class="boss-tributes-empty">No players</span>`;
    return;
  }
  const sorted = [...players].sort(
    (a, b) => (tributeMap.get(b.id) ?? 0) - (tributeMap.get(a.id) ?? 0),
  );
  const html = sorted.map((p) => {
    const tribute = tributeMap.get(p.id) ?? 0;
    const tributePct = totalTribute > 0 ? Math.round((tribute / totalTribute) * 100) : 0;
    const lane = state.lanes.find((l) => l.id === p.laneId);
    const baseHp = lane?.baseHp ?? 0;
    const baseMaxHp = lane?.baseMaxHp ?? 1;
    const hpPct = Math.max(0, Math.min(100, (baseHp / baseMaxHp) * 100));
    const hpClass = !p.isAlive ? "dead" : hpPct <= 30 ? "critical" : hpPct <= 60 ? "warning" : "";
    return `<div class="boss-tribute ${!p.isAlive ? "is-dead" : ""}">
      <div class="boss-tribute-row">
        <span class="boss-tribute-name">${escapeHtml(p.username)}${!p.isAlive ? " ☠" : ""}</span>
        <span class="boss-tribute-value">
          <span class="hp ${hpClass}">${baseHp}/${baseMaxHp}</span>
          · ${tribute.toLocaleString()}
        </span>
      </div>
      <div class="boss-tribute-bar"><div style="width:${tributePct}%"></div></div>
      <div class="boss-tribute-hp-bar"><div class="${hpClass}" style="width:${hpPct}%"></div></div>
    </div>`;
  }).join("");
  root.innerHTML = html;
}

function startBossEffectsTicker(): void {
  if (bossEffectsAnimHandle !== null) return;
  const step = () => {
    bossEffectsAnimHandle = requestAnimationFrame(step);
    if (!currentState?.bossState) {
      stopBossEffectsTicker();
      return;
    }
    renderBossEffects(currentState.bossState.activeEffects, currentState.tick);
  };
  bossEffectsAnimHandle = requestAnimationFrame(step);
}

function stopBossEffectsTicker(): void {
  if (bossEffectsAnimHandle !== null) {
    cancelAnimationFrame(bossEffectsAnimHandle);
    bossEffectsAnimHandle = null;
  }
}

function bossDisplayName(id: string): string {
  if (id === "icosahedron") return "Icosahedron";
  return id;
}

function maybeShowWaveTransition(state: GameState): void {
  if (state.waveInProgress && !lastWaveInProgress) {
    waveStartedAt = performance.now();
    const wave = buildWave(state.currentWave, state.tutorialLevel);
    let total = 0;
    let maxDelay = 0;
    for (const s of wave.spawns) {
      total += s.count;
      const slotEnd = s.delayMs + s.spacingMs * Math.max(0, s.count - 1);
      if (slotEnd > maxDelay) maxDelay = slotEnd;
    }
    waveTotalMobs = total * Math.max(1, state.lanes.length);
    waveExpectedDurationMs = Math.max(8000, maxDelay + 6000);
    waveKilled = 0;
    showBanner(`Wave ${state.currentWave}`, "Incoming", "info");
  } else if (!state.waveInProgress && lastWaveInProgress) {
    showBanner(`Wave ${state.currentWave}`, "Cleared", "success");
  }
  lastWaveInProgress = state.waveInProgress;
}

function refreshNextWave(container: HTMLElement, state: GameState): void {
  const panel = container.querySelector<HTMLDivElement>("#next-wave");
  if (!panel) return;
  const nextIndex = state.currentWave + 1;
  if (nextIndex === lastNextWaveIndex) return;
  lastNextWaveIndex = nextIndex;

  panel.querySelectorAll<HTMLCanvasElement>("canvas.mob-preview").forEach((c) => mobPreviewPool.detach(c));

  const wave = buildWave(nextIndex, state.tutorialLevel);
  const groups = new Map<MobType, number>();
  for (const spawn of wave.spawns) {
    groups.set(spawn.type, (groups.get(spawn.type) ?? 0) + spawn.count);
  }
  const laneCount = Math.max(1, state.lanes.length);

  const rows = Array.from(groups.entries())
    .map(([type, count]) => {
      const def = MOB_DEFINITIONS[type];
      const scaled = scaleMobStats(def.baseStats, nextIndex, state.tutorialLevel);
      return `
        <div class="mob-row" data-mob="${type}">
          <canvas class="mob-preview" width="32" height="32"></canvas>
          <span class="mob-name">${def.displayName}</span>
          <span class="mob-count">×${count * laneCount}</span>
          ${renderMobTooltip(def.displayName, scaled)}
        </div>
      `;
    })
    .join("");

  panel.innerHTML = `
    <div class="next-wave-header">
      <span class="label">Next wave</span>
      <span class="value">#${nextIndex}</span>
    </div>
    <div class="next-wave-list">${rows}</div>
  `;

  panel.querySelectorAll<HTMLDivElement>(".mob-row").forEach((row) => {
    const type = row.dataset.mob as MobType | undefined;
    const canvas = row.querySelector<HTMLCanvasElement>("canvas.mob-preview");
    if (canvas && type) mobPreviewPool.attach(canvas, type);
  });
}

function renderMobTooltip(name: string, stats: MobStats): string {
  const rows: string[] = [
    `<div class="row"><span class="label">HP</span><span>${stats.maxHp}</span></div>`,
    `<div class="row"><span class="label">Speed</span><span>${stats.speed.toFixed(1)}</span></div>`,
  ];
  if (stats.armor > 0) rows.push(`<div class="row"><span class="label">Armor</span><span>${stats.armor}</span></div>`);
  rows.push(`<div class="row"><span class="label">Bounty</span><span class="cost-inline">${icon("sats", 11)}${stats.bounty}</span></div>`);
  rows.push(`<div class="row"><span class="label">Base dmg</span><span>${stats.damageToBase}</span></div>`);
  const tags: string[] = [];
  if (stats.isFlying) tags.push("flying");
  return `
    <div class="mob-tooltip">
      <div class="tooltip-title">${name}${tags.length ? ` <span class="tag">${tags.join(" · ")}</span>` : ""}</div>
      <div class="tooltip-stats">${rows.join("")}</div>
    </div>
  `;
}

function renderTowerPanel(container: HTMLElement): void {
  if (!controller) return;
  const slots: TowerType[] = BASE_LOADOUT.slice();
  loadoutCache = slots;
  const panel = container.querySelector<HTMLDivElement>("#tower-panel")!;
  panel.innerHTML = slots
    .map((type, idx) => {
      const def = TOWER_DEFINITIONS[type];
      return `
        <div class="tower-tile locked" data-type="${type}" data-slot="${idx + 1}" data-locked="1">
          <span class="hotkey">${idx + 1}</span>
          <canvas class="preview" width="96" height="96"></canvas>
          <div class="name">${def.displayName}</div>
          <div class="cost">${icon("sats", 12)}${def.baseCost}</div>
          <span class="lock-overlay">${icon("lock", 18)}</span>
          ${renderTowerTooltip(type)}
        </div>
      `;
    })
    .join("");

  panel.querySelectorAll<HTMLDivElement>(".tower-tile").forEach((tile) => {
    const type = tile.dataset.type as TowerType;
    const canvas = tile.querySelector<HTMLCanvasElement>("canvas.preview");
    if (canvas) towerPreviewPool.attach(canvas, type, { locked: true });
    tile.addEventListener("click", () => toggleTowerSelection(type, container));
  });
}

function syncTowerTileLocked(container: HTMLElement): void {
  if (!controller) return;
  const me = controller.getMe();
  if (!me) return;
  const allowed = new Set<TowerType>(me.loadout);
  container.querySelectorAll<HTMLDivElement>(".tower-tile").forEach((tile) => {
    const type = tile.dataset.type as TowerType | undefined;
    if (!type) return;
    const locked = !allowed.has(type);
    tile.classList.toggle("locked", locked);
    tile.dataset.locked = locked ? "1" : "0";
    const canvas = tile.querySelector<HTMLCanvasElement>("canvas.preview");
    if (canvas) towerPreviewPool.setLocked(canvas, locked);
  });
}

function toggleTowerSelection(type: TowerType, container: HTMLElement): void {
  if (!controller) return;
  const me = controller.getMe();
  if (me && !me.loadout.includes(type)) return;
  const current = controller.getSelectedTowerType();
  controller.selectTowerType(current === type ? null : type);
  syncTowerTileSelection(container);
}

function syncTowerTileAffordability(container: HTMLElement, state: GameState, userId: string): void {
  const me = state.players.find((p) => p.id === userId);
  const gold = me?.gold ?? 0;
  container.querySelectorAll<HTMLDivElement>(".tower-tile").forEach((tile) => {
    const type = tile.dataset.type as TowerType | undefined;
    if (!type) return;
    if (tile.dataset.locked === "1") {
      tile.classList.remove("disabled");
      return;
    }
    const def = TOWER_DEFINITIONS[type];
    tile.classList.toggle("disabled", gold < def.baseCost);
  });
}

function renderTowerTooltip(type: TowerType): string {
  const def = TOWER_DEFINITIONS[type];
  const s = def.baseStats;
  const stats: string[] = [];
  if (s.damage > 0) stats.push(`<div class="row"><span class="label">Damage</span><span>${s.damage}</span></div>`);
  if (s.range > 0) stats.push(`<div class="row"><span class="label">Range</span><span>${s.range.toFixed(1)}</span></div>`);
  if (s.fireRate > 0) stats.push(`<div class="row"><span class="label">Fire rate</span><span>${s.fireRate.toFixed(2)}/s</span></div>`);
  if (s.splashRadius > 0) stats.push(`<div class="row"><span class="label">Splash</span><span>${s.splashRadius.toFixed(1)}</span></div>`);
  if (s.slowDuration > 0) stats.push(`<div class="row"><span class="label">Slow</span><span>${Math.round((1 - s.slowFactor) * 100)}% · ${s.slowDuration.toFixed(1)}s</span></div>`);
  if (s.poisonDuration > 0) stats.push(`<div class="row"><span class="label">Poison</span><span>${s.poisonDps}/s · ${s.poisonDuration.toFixed(1)}s</span></div>`);
  if (def.generatesIncome > 0) stats.push(`<div class="row"><span class="label">Income</span><span class="cost-inline">+${def.generatesIncome}${icon("sats", 11)}/wave</span></div>`);
  const targets: string[] = [];
  if (s.canTargetGround) targets.push("ground");
  if (s.canTargetAir) targets.push("air");
  if (targets.length > 0 && (s.damage > 0 || s.slowDuration > 0 || s.poisonDuration > 0)) {
    stats.push(`<div class="row"><span class="label">Targets</span><span>${targets.join(" + ")}</span></div>`);
  }
  return `
    <div class="tower-tooltip">
      <div class="tooltip-title">${def.displayName}</div>
      <div class="tooltip-desc">${def.description}</div>
      ${stats.length > 0 ? `<div class="tooltip-stats">${stats.join("")}</div>` : ""}
      <div class="tooltip-cost">Build cost <span class="cost-inline">${icon("sats", 12)}${def.baseCost}</span></div>
    </div>
  `;
}

function syncTowerTileSelection(container: HTMLElement): void {
  if (!controller) return;
  const selected = controller.getSelectedTowerType();
  container.querySelectorAll<HTMLDivElement>(".tower-tile").forEach((tile) => {
    tile.classList.toggle("selected", tile.dataset.type === selected);
  });
}

function refreshTowerInfo(container: HTMLElement): void {
  if (!controller) return;
  const tower = controller.getSelectedTower();
  const existing = container.querySelector<HTMLDivElement>("#tower-info");

  if (!tower) {
    if (existing) existing.remove();
    controller.showRangePreview(null);
    return;
  }

  const def = TOWER_DEFINITIONS[tower.type];
  const nextCost = def.upgradeCosts[tower.level];
  const myGold = controller.getMe()?.gold ?? 0;
  const canAfford = nextCost !== undefined && myGold >= nextCost;
  const upgradeDisabled = nextCost === undefined || !canAfford;
  controller.showRangePreview({ x: tower.position.x, y: tower.position.y, range: tower.stats.range });

  if (existing && existing.dataset.towerId === tower.id) {
    const titleEl = existing.querySelector<HTMLElement>(".info-title");
    if (titleEl) titleEl.textContent = `${def.displayName} · Lv ${tower.level}`;
    const dmgEl = existing.querySelector<HTMLElement>(".info-damage");
    if (dmgEl) dmgEl.textContent = String(Math.round(tower.stats.damage));
    const rangeEl = existing.querySelector<HTMLElement>(".info-range");
    if (rangeEl) rangeEl.textContent = tower.stats.range.toFixed(1);
    const rateEl = existing.querySelector<HTMLElement>(".info-firerate");
    if (rateEl) rateEl.textContent = `${tower.stats.fireRate.toFixed(2)}/s`;
    const btn = existing.querySelector<HTMLButtonElement>("#upgrade-btn");
    if (btn) {
      if (btn.disabled !== upgradeDisabled) btn.disabled = upgradeDisabled;
      const isMax = nextCost === undefined;
      btn.classList.toggle("unaffordable", !isMax && !canAfford);
      const costKey = isMax ? "max" : String(nextCost);
      if (btn.dataset.costKey !== costKey) {
        btn.dataset.costKey = costKey;
        btn.innerHTML = nextCost
          ? `Upgrade <span class="cost-inline">${icon("sats", 11)}${nextCost}</span>`
          : "Max";
      }
    }
    existing.querySelectorAll<HTMLButtonElement>(".targeting-group button").forEach((b) => {
      b.classList.toggle("active", b.dataset.mode === tower.targetingMode);
    });
    return;
  }

  if (existing) existing.remove();

  const div = document.createElement("div");
  div.className = "tower-info";
  div.id = "tower-info";
  div.dataset.towerId = tower.id;
  const modes: TargetingMode[] = ["first", "last", "strongest", "weakest", "closest"];
  div.innerHTML = `
    <div class="info-title">${def.displayName} · Lv ${tower.level}</div>
    <div class="row"><span class="label">Damage</span><span class="info-damage">${Math.round(tower.stats.damage)}</span></div>
    <div class="row"><span class="label">Range</span><span class="info-range">${tower.stats.range.toFixed(1)}</span></div>
    <div class="row"><span class="label">Fire rate</span><span class="info-firerate">${tower.stats.fireRate.toFixed(2)}/s</span></div>
    <div class="targeting">
      <span class="label">Targeting</span>
      <div class="targeting-group">
        ${modes.map((m) => `<button data-mode="${m}" class="${m === tower.targetingMode ? "active" : ""}">${m}</button>`).join("")}
      </div>
    </div>
    <div class="actions">
      <button id="upgrade-btn" class="primary ${!canAfford && nextCost !== undefined ? "unaffordable" : ""}" data-cost-key="${nextCost === undefined ? "max" : nextCost}" ${upgradeDisabled ? "disabled" : ""}>${nextCost ? `Upgrade <span class="cost-inline">${icon("sats", 11)}${nextCost}</span>` : "Max"}</button>
      <button id="sell-btn" class="danger">Sell</button>
    </div>
  `;
  container.appendChild(div);
  div.querySelector("#upgrade-btn")!.addEventListener("click", () => controller!.upgradeSelected());
  div.querySelector("#sell-btn")!.addEventListener("click", () => controller!.sellSelected());
  div.querySelectorAll<HTMLButtonElement>(".targeting-group button").forEach((b) => {
    b.addEventListener("click", () => {
      const mode = b.dataset.mode as TargetingMode;
      controller!.setTargetingMode(mode);
    });
  });
}

function handleGameEvent(event: GameEvent): void {
  switch (event.kind) {
    case "wave_start":
      appendEvent(`Wave ${event.wave} incoming`, "warning");
      break;
    case "wave_cleared":
      appendEvent(`Wave ${event.wave} cleared`, "success");
      break;
    case "wave_skipped": {
      const me = controller?.getMe();
      const mine = me ? event.bonuses.find((b) => b.playerId === me.id) : undefined;
      if (mine && mine.gold > 0) {
        appendEvent(`Skip bonus +${mine.gold} (saved ${event.secondsSaved}s)`, "success");
      }
      break;
    }
    case "player_eliminated":
      appendEvent("Player eliminated", "danger");
      break;
    case "boss_spawn":
      appendEvent("Boss approaching", "boss");
      showBanner("Boss", "Approaching", "boss");
      controller?.shake(0.7, 800);
      break;
    case "mob_reached_base": {
      // Only the affected lane's owner should see "Base breach" + camera
      // shake. Other players (incl. spectators) get no shake — the previous
      // behaviour rattled the camera every time anyone took a hit.
      const me = controller?.getMe();
      const myLaneId = me?.laneId ?? null;
      const spectated = controller?.spectatedLane() ?? null;
      const focusLane = spectated ?? myLaneId;
      const isMyLane = me && event.laneId === myLaneId;
      if (isMyLane) {
        appendEvent(`Base breach -${event.damage}`, "danger");
        controller?.shake(0.35, 220);
      } else if (focusLane && event.laneId === focusLane) {
        // Spectator looking at this lane: tiny shake so the impact reads, but
        // gentler since it's not the player's own base.
        controller?.shake(0.15, 160);
      }
      break;
    }
    case "mob_killed":
      waveKilled++;
      matchStats.mobsKilled++;
      break;
    case "tower_placed":
      matchStats.towersBuilt++;
      break;
    case "tower_sold":
      break;
    case "tutorial_hint":
      showTutorialHint(event.text);
      break;
    case "tutorial_complete":
      invalidateTutorialCache();
      showBanner(`Training ${event.level} cleared`, `Unlocked: ${event.rewards.join(", ") || "—"}`, "success");
      break;
  }
}

function showTutorialHint(text: string): void {
  if (!bannerLayer) return;
  const wrap = document.createElement("div");
  wrap.className = "tutorial-hint";
  wrap.innerHTML = `<span class="hint-icon">${icon("wave", 14)}</span><span>${escapeHtml(text)}</span>`;
  bannerLayer.appendChild(wrap);
  setTimeout(() => wrap.remove(), 7000);
}

function appendEvent(text: string, severity: Severity = "info"): void {
  if (!eventLogContainer) return;
  const el = document.createElement("div");
  el.className = `event severity-${severity}`;
  const iconName: Record<Severity, string> = {
    info: "wave",
    success: "trophy",
    warning: "wave",
    danger: "hp",
    boss: "hp",
  };
  el.innerHTML = `<span class="ev-icon">${icon(iconName[severity], 14)}</span><span>${escapeHtml(text)}</span>`;
  eventLogContainer.appendChild(el);
  const lifetime = severity === "boss" ? 9000 : severity === "danger" ? 7800 : 5500;
  setTimeout(() => el.remove(), lifetime);
}

function showBanner(title: string, subtitle: string, severity: Severity): void {
  if (!bannerLayer) return;
  const wrap = document.createElement("div");
  wrap.className = `banner-overlay ${severity === "danger" ? "danger" : severity === "success" ? "success" : severity === "boss" ? "boss" : ""}`;
  wrap.innerHTML = `<div class="title">${escapeHtml(title)}</div><div class="subtitle">${escapeHtml(subtitle)}</div>`;
  bannerLayer.appendChild(wrap);
  const lifetime = severity === "boss" ? 3200 : 2400;
  setTimeout(() => wrap.remove(), lifetime);
}

function startPlayerLabelLoop(userId: string): void {
  if (labelRafHandle !== null) cancelAnimationFrame(labelRafHandle);
  const tick = () => {
    refreshPlayerLabels(userId);
    labelRafHandle = requestAnimationFrame(tick);
  };
  labelRafHandle = requestAnimationFrame(tick);
}

function stopPlayerLabelLoop(): void {
  if (labelRafHandle !== null) {
    cancelAnimationFrame(labelRafHandle);
    labelRafHandle = null;
  }
  if (labelLayer) labelLayer.innerHTML = "";
  playerLabelEls.clear();
}

function refreshPlayerLabels(userId: string): void {
  if (!labelLayer || !controller || !currentState) return;
  const isBoss =
    currentState.mode === "boss_hunt" || currentState.mode === "boss_endurance";
  if (!isBoss) {
    // Only show labels in boss/spectator-prone modes.
    if (playerLabelEls.size > 0) {
      labelLayer.innerHTML = "";
      playerLabelEls.clear();
    }
    return;
  }
  const spectatedLane = controller.spectatedLane();
  const seen = new Set<string>();
  for (const lane of currentState.lanes) {
    if (!lane.ownerId) continue;
    const owner = currentState.players.find((p) => p.id === lane.ownerId);
    if (!owner) continue;
    seen.add(lane.id);
    const proj = controller.projectFlatToViewport(
      lane.baseCell.cx + 0.5,
      lane.baseCell.cy + 0.5,
    );
    let el = playerLabelEls.get(lane.id);
    if (!el) {
      el = document.createElement("div");
      el.className = "player-label";
      labelLayer.appendChild(el);
      playerLabelEls.set(lane.id, el);
    }
    const isMe = owner.id === userId;
    const isSpectated = spectatedLane === lane.id;
    const isDead = !owner.isAlive;
    const classes = ["player-label"];
    if (isMe) classes.push("self");
    if (isSpectated) classes.push("spectated");
    if (isDead) classes.push("dead");
    if (!proj.onScreen) classes.push("offscreen");
    el.className = classes.join(" ");
    const tagText = isMe ? "YOU" : (isSpectated ? "WATCHING" : "");
    el.innerHTML = `
      ${tagText ? `<span class="player-label-tag">${tagText}</span>` : ""}
      <span class="player-label-name">${escapeHtml(owner.username)}</span>
      ${!proj.onScreen ? `<span class="player-label-arrow" style="transform:rotate(${proj.angleFromCenter}rad)">▸</span>` : ""}
    `;
    el.style.left = `${proj.sx}px`;
    el.style.top = `${proj.sy}px`;
  }
  // Garbage-collect labels for lanes that no longer exist (shouldn't happen
  // mid-match but keeps things tidy).
  for (const [laneId, el] of playerLabelEls) {
    if (!seen.has(laneId)) {
      el.remove();
      playerLabelEls.delete(laneId);
    }
  }
}

function spawnDamageNumber(worldX: number, worldY: number, amount: number, isKill: boolean): void {
  if (!dmgLayer || !controller) return;
  const screen = controller.worldToScreen(worldX, worldY);
  if (!screen) return;
  const el = document.createElement("div");
  el.className = `dmg-number${isKill ? " crit" : ""}`;
  el.textContent = `-${Math.round(amount)}`;
  el.style.left = `${screen.x}px`;
  el.style.top = `${screen.y}px`;
  dmgLayer.appendChild(el);
  matchStats.damageDealt += amount;
  setTimeout(() => el.remove(), 950);
}

function setupHotkeys(container: HTMLElement): void {
  if (hotkeyHandler) window.removeEventListener("keydown", hotkeyHandler);
  hotkeyHandler = (e: KeyboardEvent) => {
    const tag = (e.target as HTMLElement | null)?.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA") return;
    if (e.code === "Space") {
      e.preventDefault();
      controller?.sendReady();
      return;
    }
    if (e.code === "Escape") {
      controller?.selectTowerType(null);
      controller?.clearTowerSelection();
      syncTowerTileSelection(container);
      if (currentState) controller && refreshTowerInfo(container);
      return;
    }
    // Spectator cycle — only active when the player is dead in boss mode
    // AND there's more than one survivor to choose between.
    if (e.code === "ArrowLeft" || e.code === "ArrowRight") {
      const me = controller?.getMe();
      const isBoss =
        currentState?.mode === "boss_hunt" || currentState?.mode === "boss_endurance";
      if (isBoss && me && !me.isAlive) {
        const aliveCount = currentState?.players.filter((p) => p.isAlive).length ?? 0;
        if (aliveCount <= 1) return; // nothing to cycle through
        e.preventDefault();
        // Pressing → should rotate the camera/view to the right, which means
        // spectating the previous lane in radial order (seats are numbered
        // CCW so seat-1 sits to the screen-right of seat-0).
        const dir = e.code === "ArrowRight" ? -1 : 1;
        const newLaneId = controller?.cycleSpectator(dir);
        renderSpectatorOverlay(container, newLaneId ?? null);
        return;
      }
    }
    const n = Number(e.key);
    if (Number.isInteger(n) && n >= 1 && n <= 8) {
      const type = loadoutCache[n - 1];
      if (!type) return;
      const me = controller?.getMe();
      if (me && !me.loadout.includes(type)) return;
      toggleTowerSelection(type, container);
    }
  };
  window.addEventListener("keydown", hotkeyHandler);
}

function maybeAutoSpectate(container: HTMLElement, state: GameState, userId: string): void {
  const isBoss = state.mode === "boss_hunt" || state.mode === "boss_endurance";
  if (!isBoss) return;
  const me = state.players.find((p) => p.id === userId);
  if (!me) return;
  // Edge-trigger when we just died: prompt the user with a death choice.
  if (wasAlive && !me.isAlive) {
    wasAlive = false;
    renderDeathChoice(container, state, userId);
  } else if (!wasAlive && me.isAlive) {
    wasAlive = true;
    controller?.resetSpectator();
    renderSpectatorOverlay(container, null);
    removeDeathChoice(container);
  }

  // Auto-hop when the player we're spectating dies. Without this the camera
  // stays glued to a corpse lane until the user presses an arrow key.
  const spectatedLaneId = controller?.spectatedLane();
  if (spectatedLaneId) {
    const lane = state.lanes.find((l) => l.id === spectatedLaneId);
    const owner = lane?.ownerId
      ? state.players.find((p) => p.id === lane.ownerId)
      : null;
    if (!owner || !owner.isAlive) {
      const newTarget = controller?.cycleSpectator(1) ?? null;
      renderSpectatorOverlay(container, newTarget);
    }
  }
}

function removeDeathChoice(container: HTMLElement): void {
  container.querySelector<HTMLDivElement>("#death-choice")?.remove();
}

function renderDeathChoice(container: HTMLElement, state: GameState, userId: string): void {
  removeDeathChoice(container);
  // Count alive opponents to decide whether spectating is offered.
  const aliveOthers = state.players.filter((p) => p.id !== userId && p.isAlive).length;
  const overlay = document.createElement("div");
  overlay.id = "death-choice";
  overlay.className = "death-choice";
  overlay.innerHTML = `
    <div class="death-choice-card">
      <div class="death-choice-icon">☠</div>
      <div class="death-choice-title">You fell</div>
      <div class="death-choice-sub">${
        aliveOthers > 0
          ? "Stay and watch the others, or head back to the lobby."
          : "Your base is gone. The match will end shortly."
      }</div>
      <div class="death-choice-actions">
        <button class="btn death-choice-leave" type="button">Return to lobby</button>
        ${aliveOthers > 0 ? `<button class="btn primary death-choice-spectate" type="button">Spectate</button>` : ""}
      </div>
    </div>
  `;
  container.appendChild(overlay);

  const leaveBtn = overlay.querySelector<HTMLButtonElement>(".death-choice-leave");
  leaveBtn?.addEventListener("click", () => {
    removeDeathChoice(container);
    leaveMatch();
  });

  const spectateBtn = overlay.querySelector<HTMLButtonElement>(".death-choice-spectate");
  spectateBtn?.addEventListener("click", () => {
    removeDeathChoice(container);
    const targetLane = controller?.cycleSpectator(1) ?? null;
    if (targetLane) renderSpectatorOverlay(container, targetLane);
  });
}

function renderSpectatorOverlay(container: HTMLElement, laneId: string | null): void {
  let overlay = container.querySelector<HTMLDivElement>("#spectator-overlay");
  if (!laneId) {
    overlay?.remove();
    return;
  }
  const state = currentState;
  if (!state) return;
  const lane = state.lanes.find((l) => l.id === laneId);
  const owner = lane?.ownerId
    ? state.players.find((p) => p.id === lane.ownerId)
    : null;
  const name = owner?.username ?? "—";
  if (!overlay) {
    overlay = document.createElement("div");
    overlay.id = "spectator-overlay";
    overlay.className = "spectator-overlay";
    container.appendChild(overlay);
  }
  const aliveCount = state.players.filter((p) => p.isAlive).length;
  const hint = aliveCount > 1
    ? "← / → to switch player · ESC closes"
    : "Last survivor · ESC closes";
  overlay.innerHTML = `
    <div class="spectator-row">
      <span class="spectator-label">Spectating</span>
      <span class="spectator-name">${escapeHtml(name)}</span>
    </div>
    <div class="spectator-hint">${hint}</div>
  `;
}

function showGameOver(winnerId: string | null, userId: string): void {
  // Match ended — death-choice prompt is no longer relevant.
  if (matchContainer) removeDeathChoice(matchContainer);
  const won = winnerId === userId;
  const isDefeat = !won;
  const overlay = document.createElement("div");
  overlay.className = `game-over-screen ${isDefeat ? "defeat-bg" : "victory-bg"}`;
  const isBossMode =
    currentState?.mode === "boss_hunt" || currentState?.mode === "boss_endurance";
  const bossDefeated = isBossMode && winnerId === null
    ? (currentState?.bossState?.hp ?? 1) <= 0
    : false;
  const result = isBossMode
    ? bossDefeated
      ? "Boss Slain"
      : winnerId === null
        ? "Wipe"
        : won
          ? "Victory"
          : "Defeat"
    : winnerId === null
      ? "Defeat"
      : won
        ? "Victory"
        : "Defeat";
  const sub = won
    ? "Match complete · Well played"
    : bossDefeated
      ? "Boss defeated — pot split among survivors by tribute"
      : isBossMode && winnerId === null
        ? "The boss outlasted you — stakes burned"
        : "Match complete · Better luck next time";
  const me = currentState?.players.find((p) => p.id === userId);
  const score = me?.score ?? 0;
  const myLane = me ? currentState?.lanes.find((l) => l.id === me.laneId) : null;
  const lane = myLane ?? currentState?.lanes[0];
  const baseHpStat = won && lane
    ? `<div class="stat"><div class="label">Base HP</div><div class="value">${lane.baseHp}/${lane.baseMaxHp}</div></div>`
    : "";

  const bossSummary = isBossMode ? renderBossSummary(userId) : "";

  overlay.innerHTML = `
    <div class="game-over-card ${won ? "victory" : "defeat"}">
      <div class="result">${result}</div>
      <div class="sub">${sub}</div>
      <div class="stats-grid">
        <div class="stat"><div class="label">Wave</div><div class="value">${currentState?.currentWave ?? 0}</div></div>
        <div class="stat"><div class="label">Score</div><div class="value">${score.toLocaleString()}</div></div>
        <div class="stat"><div class="label">Kills</div><div class="value">${matchStats.mobsKilled}</div></div>
        <div class="stat"><div class="label">Towers</div><div class="value">${matchStats.towersBuilt}</div></div>
        <div class="stat"><div class="label">Damage</div><div class="value">${Math.round(matchStats.damageDealt).toLocaleString()}</div></div>
        ${baseHpStat}
      </div>
      ${bossSummary}
      <div class="btn-row" style="justify-content: center;">
        <button id="return-btn" class="primary">Return to lobby</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
  overlay.querySelector("#return-btn")!.addEventListener("click", () => {
    overlay.remove();
    leaveMatch();
  });
}

function renderBossSummary(userId: string): string {
  if (tributesByUser.length === 0) {
    return `<div class="boss-summary"><div class="boss-summary-title">No tribute recorded</div></div>`;
  }
  const total = tributesByUser.reduce((sum, t) => sum + t.totalTribute, 0);
  const sorted = [...tributesByUser].sort((a, b) => b.totalTribute - a.totalTribute);
  const rows = sorted.map((t, idx) => {
    const player = currentState?.players.find((p) => p.id === t.userId);
    const name = player?.username ?? t.userId.slice(0, 8);
    const pct = total > 0 ? Math.round((t.totalTribute / total) * 100) : 0;
    const isMe = t.userId === userId;
    const isAlive = player?.isAlive ?? false;
    return `<div class="boss-summary-row ${isMe ? "me" : ""}">
      <span class="rank">#${idx + 1}</span>
      <span class="name">${escapeHtml(name)}${isAlive ? "" : ' <span class="dead">(eliminated)</span>'}</span>
      <span class="tribute">${t.totalTribute.toLocaleString()}</span>
      <span class="pct">${pct}%</span>
    </div>`;
  }).join("");
  return `<div class="boss-summary">
    <div class="boss-summary-title">Tribute ranking</div>
    ${rows}
  </div>`;
}

function leaveMatch(): void {
  if (matchContainer) removeDeathChoice(matchContainer);
  matchContainer = null;
  stopPlayerLabelLoop();
  labelLayer = null;
  towerPreviewPool.destroy();
  mobPreviewPool.destroy();
  gameSocket.disconnect();
  controller?.destroy();
  controller = null;
  lastNextWaveIndex = -1;
  if (hotkeyHandler) {
    window.removeEventListener("keydown", hotkeyHandler);
    hotkeyHandler = null;
  }
  appState.set({ screen: "lobby", matchAssignment: null });
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === "&" ? "&amp;" :
    c === "<" ? "&lt;" :
    c === ">" ? "&gt;" :
    c === '"' ? "&quot;" : "&#39;",
  );
}

export function isMobSendable(type: MobType): boolean {
  return MOB_DEFINITIONS[type].sendCost !== null;
}
